// client/src/settings/SettingsPage.tsx
import React, { useState, useEffect } from 'react';
import { 
    useDataProvider, 
    useNotify, 
    useRedirect, 
    Title 
} from 'react-admin';
import {
    Box,
    Typography,
    FormGroup,
    FormControlLabel,
    Switch,
    TextField,
    Button,
} from '@mui/material';

const SettingsPage: React.FC = () => {
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const redirect = useRedirect();

    const [loading, setLoading] = useState(true);
    const [enabled, setEnabled] = useState(false);
    const [amount, setAmount] = useState('');
    const [saving, setSaving] = useState(false);

    // on mount, fetch both settings
    useEffect(() => {
        dataProvider
            .getList('system_settings', {
                pagination: { page: 1, perPage: 50 },
                sort: { field: 'setting_key', order: 'ASC' },
                filter: {},
            })
            .then(({ data }) => {
                const add = data.find(s => s.setting_key === 'ADD_TO_GLOBAL_PRICE');
                setEnabled(Boolean(add?.active));
                const val = data.find(s => s.setting_key === 'GLOBAL_PRICE_ADD_VALUE');
                setAmount(val?.setting_value ?? '');
            })
            .catch(error => {
                console.error(error);
                notify('Error loading settings', { type: 'warning' });
            })
            .finally(() => setLoading(false));
    }, [dataProvider, notify]);

    const handleSave = () => {
        setSaving(true);
        // two parallel updates
        Promise.all([
            dataProvider.update('system_settings', {
                id: 'ADD_TO_GLOBAL_PRICE',
                data: { setting_value: enabled ? 'TRUE' : 'FALSE', active: enabled },
                previousData: {},
            }),
            dataProvider.update('system_settings', {
                id: 'GLOBAL_PRICE_ADD_VALUE',
                data: { setting_value: amount, active: true },
                previousData: {},
            }),
        ])
            .then(() => {
                notify('Settings saved', { type: 'info' });
            })
            .catch(error => {
                console.error(error);
                notify(`Error saving settings: ${error.message}`, { type: 'warning' });
            })
            .finally(() => setSaving(false));
    };

    if (loading) {
        return <Typography>Loading settings…</Typography>;
    }

    return (
        <Box p={3} maxWidth={400}>
            <Title title="System Settings" />
            <FormGroup>
                <FormControlLabel
                    control={
                        <Switch
                            checked={enabled}
                            onChange={e => setEnabled(e.target.checked)}
                        />
                    }
                    label="Enable Global Price Increase"
                />
            </FormGroup>

            <Box mt={2}>
                <TextField
                    label="Global Price Increase Amount"
                    value={amount}
                    onChange={e => {
                        const v = e.target.value;
                        if (/^\d*\.?\d{0,2}$/.test(v)) setAmount(v);
                    }}
                    disabled={!enabled}
                    fullWidth
                    inputProps={{
                        inputMode: 'decimal',
                        pattern: '^\\d*\\.?\\d{0,2}$',
                    }}
                />
            </Box>

            <Box mt={3}>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={saving}
                    fullWidth
                >
                    {saving ? 'Saving…' : 'Save'}
                </Button>
            </Box>
        </Box>
    );
};

export default SettingsPage;